import { useEffect, useState } from 'react';
import { Monitor, Loader2, AlertCircle, Trash2, Shield } from 'lucide-react';
import { sessionsApi } from '../../../api/client';
import type { UserSession } from '../../../api/client';

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Unknown';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function parseUserAgent(ua: string | undefined): string {
  if (!ua) return 'Unknown device';
  if (/Chrome/i.test(ua)) return 'Chrome';
  if (/Firefox/i.test(ua)) return 'Firefox';
  if (/Safari/i.test(ua)) return 'Safari';
  if (/Edge/i.test(ua)) return 'Microsoft Edge';
  return 'Browser';
}

export function SessionsSettingsPage() {
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revoking, setRevoking] = useState<string | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);

  useEffect(() => {
    setLoading(true);
    sessionsApi.list()
      .then(setSessions)
      .catch(() => setError('Failed to load sessions'))
      .finally(() => setLoading(false));
  }, []);

  async function handleRevoke(sessionId: string) {
    setRevoking(sessionId);
    try {
      await sessionsApi.revoke(sessionId);
      setSessions((s) => s.filter((session) => session.id !== sessionId));
    } catch {
      setError('Failed to revoke session');
    } finally {
      setRevoking(null);
    }
  }

  async function handleRevokeAll() {
    setRevokingAll(true);
    try {
      await sessionsApi.revokeOthers();
      setSessions((s) => s.filter((session) => session.isCurrent));
    } catch {
      setError('Failed to revoke sessions');
    } finally {
      setRevokingAll(false);
    }
  }

  const otherSessions = sessions.filter((s) => !s.isCurrent);

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-100">Sessions</h2>
        <p className="text-sm text-gray-500 mt-1">View and manage your active sessions across devices.</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 bg-loss/10 border border-loss/30 rounded-md text-sm text-loss">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-brand" />
        </div>
      ) : (
        <div className="card divide-y divide-surface-4">
          {sessions.length === 0 && (
            <div className="px-6 py-8 text-center text-sm text-gray-500">
              No active sessions found
            </div>
          )}
          {sessions.map((session) => (
            <div key={session.id} className="flex items-center gap-4 px-6 py-4">
              <div className="w-9 h-9 rounded-full bg-surface-3 flex items-center justify-center flex-shrink-0">
                <Monitor className="w-4 h-4 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-200">
                    {parseUserAgent(session.userAgent)}
                  </p>
                  {session.isCurrent && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-brand/15 text-brand border border-brand/30">
                      <Shield className="w-2.5 h-2.5" />
                      Current
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {session.userAgent ?? 'Unknown browser'} · Last active {formatDate(session.lastActive)}
                </p>
              </div>
              {!session.isCurrent && (
                <button
                  onClick={() => handleRevoke(session.id)}
                  disabled={revoking === session.id}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-loss hover:bg-loss/10 rounded-md transition-colors disabled:opacity-50"
                >
                  {revoking === session.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3" />
                  )}
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {otherSessions.length > 0 && (
        <div className="flex justify-end">
          <button
            onClick={handleRevokeAll}
            disabled={revokingAll}
            className="flex items-center gap-2 text-sm text-loss hover:text-loss/80 transition-colors disabled:opacity-50"
          >
            {revokingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Revoke all other sessions
          </button>
        </div>
      )}

      <div className="op-panel p-4 bg-surface-2/50">
        <p className="text-xs text-gray-500">
          <strong className="text-gray-400">Note:</strong> Revoking a session signs that device out immediately.
          The current session will remain active. If you believe your account has been compromised,
          revoke all other sessions and update your password.
        </p>
      </div>
    </div>
  );
}
